import React, {
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';

import withProjectSidebar from '../../../components/with-project-sidebar';
import Tabs from '../../../constants/tabs';
import ParticipantsSource from '../../../../core/constants/participantsSource';
import AnalyticsQuestions from './unmoderated/questions';
import Demographics from './unmoderated/demographics';

import {
  Dropdown,
  TabMenu,
} from './styles';

import {
  TAB_NAME,
  DEMO_ID,
} from './constants';

import TestTypes from '../../../../core/constants/testTypes';
import EmptyResult from '../components/empty-result';
import AnalyticsQuotes from './unmoderated/quotes';

const ProjectAnalytics = (props) => {
  const { project, history } = props;

  const [segmentId, setSegmentId] = useState('all');

  const { testType, processedTestersNumber, segments } = project || {};
  const isDemoProject = project?.id === DEMO_ID;

  const isAnyTesterApprovedInSegmentId = useMemo(() => {
    if (segmentId === 'all') return processedTestersNumber > 0;

    const segment = _.find(segments, { id: segmentId });
    return segment?.countApprovedTesters > 0;
  }, [segmentId, segments, processedTestersNumber]);

  const tabItems = useMemo(() => {
    if (!project) return [];

    const items = [];
    const isPux = project.participantsSource === ParticipantsSource.PLAYBOOK;
    const isModerated = project.testType === TestTypes.MODERATED;

    if (!isModerated) {
      items.push(
        {
          menuItem: TAB_NAME.questions,
          render: () => (
            <AnalyticsQuestions
              key="questions"
              project={project}
              segmentId={segmentId}
              isDemoProject={isDemoProject}
            />
          ),
        },
        {
          menuItem: TAB_NAME.quotes,
          render: () => (
            <AnalyticsQuotes
              key="quotes"
              history={history}
              project={project}
              segmentId={segmentId}
              isDemoProject={isDemoProject}
            />
          ),
        },
      );
    }

    if ((isModerated && isPux) || isPux) {
      items.push({
        menuItem: TAB_NAME.demographics,
        render: () => (
          <Demographics
            key="demographics"
            isDemoProject={isDemoProject}
            projectId={project.id}
            segmentId={segmentId}
          />
        ),
      });
    }

    return items;
  }, [history, isDemoProject, project, segmentId]);

  const segmentsDropdowOptions = [
    { key: 1, text: 'All audience segments', value: 'all' },
    ..._.map(project.segments, (segment) => ({
      key: segment.id,
      text: segment.name,
      value: segment.id,
    })),
  ];

  const renderContent = () => {
    if (!isAnyTesterApprovedInSegmentId) {
      return (
        <EmptyResult testType={testType} />
      );
    }

    return (
      <TabMenu menu={{ secondary: true }} panes={tabItems} defaultActiveIndex={0} />
    );
  };

  return (
    <>
      {project.segments.length > 1 && (
        <Dropdown
          placeholder="Select one segment"
          fluid
          selection
          value={segmentId}
          defaultValue="all"
          onChange={(event, data) => setSegmentId(data.value)}
          options={segmentsDropdowOptions}
        />
      )}

      {renderContent()}
    </>
  );
};

ProjectAnalytics.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,

  project: PropTypes.shape({
    id: PropTypes.string,
    testers: PropTypes.arrayOf(PropTypes.shape()),
    surveys: PropTypes.arrayOf(PropTypes.shape()),
    segments: PropTypes.arrayOf(PropTypes.shape()),
    testType: PropTypes.string,
    participantsSource: PropTypes.string,
    status: PropTypes.string,
    processedTestersNumber: PropTypes.number,
  }).isRequired,
};

export default compose(
  withRouter,
  withProjectSidebar({ selectedTab: Tabs.PROJECT }),
)(ProjectAnalytics);
